import type { DocPage, NavCategory } from './types'
import type { ManualLocale } from '../i18n/locale'
import deDictionary from '../i18n/de.json'
import frDictionary from '../i18n/fr.json'
import { dictionaryFor, localizeNavigation, localizePage } from './localize'
import { introduction } from './pages/introduction'
import { gettingStarted } from './pages/getting-started'
import { account } from './pages/account'
import { workspace } from './pages/workspace'
import { configs } from './pages/configs'
import { modelTools } from './pages/model-tools'
import { mapView } from './pages/map-view'
import { logicalObjects } from './pages/logical-objects'
import { settings } from './pages/settings'
import { formats } from './pages/formats'
import { postgis } from './pages/postgis'
import { smallworld } from './pages/smallworld'
import { autocad } from './pages/autocad'
import { fileFormats } from './pages/file-formats'
import { textCsv } from './pages/text-csv'
import { styles } from './pages/styles'
import { mapping } from './pages/mapping'
import { fixedMapping } from './pages/fixed-mapping'
import { valueMapping } from './pages/value-mapping'
import { automap } from './pages/automap'
import { connectKeys } from './pages/connect-keys'
import { transfer } from './pages/transfer'
import { profiles } from './pages/profiles'
import { glossary } from './pages/glossary'
import { troubleshooting } from './pages/troubleshooting'

/** Ordered for prev/next navigation. */
export const pages: DocPage[] = [
  introduction,
  gettingStarted,
  account,
  workspace,
  configs,
  modelTools,
  mapView,
  logicalObjects,
  settings,
  formats,
  postgis,
  smallworld,
  autocad,
  fileFormats,
  textCsv,
  styles,
  mapping,
  fixedMapping,
  valueMapping,
  automap,
  connectKeys,
  transfer,
  profiles,
  glossary,
  troubleshooting,
]

export const pageMap = Object.fromEntries(pages.map((p) => [p.id, p])) as Record<
  string,
  DocPage
>

export const navigation: NavCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    pages: [
      { id: 'introduction', title: 'Introduction' },
      { id: 'getting-started', title: 'Getting started' },
      { id: 'account', title: 'Account & subscription' },
    ],
  },
  {
    id: 'workspace',
    title: 'Workspace',
    pages: [
      { id: 'workspace', title: 'Workspace tour' },
      { id: 'configs', title: 'Model configurations' },
      { id: 'model-tools', title: 'Model tree & filters' },
      { id: 'map-view', title: 'Map view & attribute table' },
      { id: 'logical-objects', title: 'Logical objects' },
      { id: 'settings', title: 'Settings' },
    ],
  },
  {
    id: 'formats',
    title: 'Formats',
    pages: [
      { id: 'formats', title: 'Formats overview' },
      { id: 'postgis', title: 'PostGIS' },
      { id: 'smallworld', title: 'Smallworld' },
      { id: 'autocad', title: 'AutoCAD' },
      { id: 'file-formats', title: 'File formats' },
      { id: 'text-csv', title: 'Text / CSV / Excel' },
      { id: 'styles', title: 'Styles import & export' },
    ],
  },
  {
    id: 'mapping',
    title: 'Mapping',
    pages: [
      { id: 'mapping', title: 'Mapping overview' },
      { id: 'fixed-mapping', title: 'Fixed mapping' },
      { id: 'value-mapping', title: 'Value mapping' },
      { id: 'automap', title: 'Auto Map' },
      { id: 'connect-keys', title: 'Connect keys' },
    ],
  },
  {
    id: 'transfer',
    title: 'Transfer',
    pages: [
      { id: 'transfer', title: 'Running transfers' },
      { id: 'profiles', title: 'Profiles & history' },
    ],
  },
  {
    id: 'reference',
    title: 'Reference',
    pages: [
      { id: 'glossary', title: 'Glossary' },
      { id: 'troubleshooting', title: 'Troubleshooting' },
    ],
  },
]

const dictionaries = {
  de: deDictionary as Record<string, string>,
  fr: frDictionary as Record<string, string>,
}

const catalogCache = new Map<
  ManualLocale,
  { pages: DocPage[]; pageMap: Record<string, DocPage>; navigation: NavCategory[] }
>()

export function catalog(locale: ManualLocale = 'en') {
  const cached = catalogCache.get(locale)
  if (cached) return cached
  const dict = dictionaryFor(locale, dictionaries)
  const localizedPages = pages.map((page) => localizePage(page, dict))
  const result = {
    pages: localizedPages,
    pageMap: Object.fromEntries(localizedPages.map((page) => [page.id, page])) as Record<
      string,
      DocPage
    >,
    navigation: localizeNavigation(navigation, localizedPages, dict),
  }
  catalogCache.set(locale, result)
  return result
}

export function getPage(id: string, locale: ManualLocale = 'en'): DocPage | undefined {
  return catalog(locale).pageMap[id]
}

export function getAdjacentPages(
  id: string,
  locale: ManualLocale = 'en',
): {
  prev?: { id: string; title: string }
  next?: { id: string; title: string }
} {
  const localized = catalog(locale).pages
  const index = localized.findIndex((p) => p.id === id)
  if (index < 0) return {}
  return {
    prev:
      index > 0
        ? { id: localized[index - 1].id, title: localized[index - 1].title }
        : undefined,
    next:
      index < localized.length - 1
        ? { id: localized[index + 1].id, title: localized[index + 1].title }
        : undefined,
  }
}

export interface SearchHit {
  pageId: string
  pageTitle: string
  category: string
  sectionId?: string
  sectionTitle?: string
  snippet: string
}

export function searchDocs(
  query: string,
  locale: ManualLocale = 'en',
  limit = 24,
): SearchHit[] {
  const q = query.trim().toLowerCase()
  if (!q) return []

  const hits: SearchHit[] = []

  for (const page of catalog(locale).pages) {
    if (
      page.title.toLowerCase().includes(q) ||
      page.description.toLowerCase().includes(q)
    ) {
      hits.push({
        pageId: page.id,
        pageTitle: page.title,
        category: page.category,
        snippet: page.description,
      })
    }

    for (const block of page.blocks) {
      if (hits.length >= limit) return hits

      if (block.type === 'heading' && block.text.toLowerCase().includes(q)) {
        hits.push({
          pageId: page.id,
          pageTitle: page.title,
          category: page.category,
          sectionId: block.id,
          sectionTitle: block.text,
          snippet: block.text,
        })
      }

      if (block.type === 'paragraph' && block.text.toLowerCase().includes(q)) {
        hits.push({
          pageId: page.id,
          pageTitle: page.title,
          category: page.category,
          snippet: excerpt(block.text, q),
        })
      }

      if (block.type === 'list') {
        for (const item of block.items) {
          if (item.toLowerCase().includes(q)) {
            hits.push({
              pageId: page.id,
              pageTitle: page.title,
              category: page.category,
              snippet: excerpt(item, q),
            })
            break
          }
        }
      }

      if (block.type === 'table') {
        for (const row of block.rows) {
          const joined = row.join(' ')
          if (joined.toLowerCase().includes(q)) {
            hits.push({
              pageId: page.id,
              pageTitle: page.title,
              category: page.category,
              snippet: excerpt(joined, q),
            })
            break
          }
        }
      }

      if (block.type === 'definitions') {
        for (const item of block.items) {
          if (
            item.term.toLowerCase().includes(q) ||
            item.definition.toLowerCase().includes(q)
          ) {
            hits.push({
              pageId: page.id,
              pageTitle: page.title,
              category: page.category,
              snippet: `${item.term} — ${item.definition}`,
            })
          }
        }
      }

      if (block.type === 'steps') {
        for (const item of block.items) {
          if (item.toLowerCase().includes(q)) {
            hits.push({
              pageId: page.id,
              pageTitle: page.title,
              category: page.category,
              snippet: excerpt(item, q),
            })
            break
          }
        }
      }

      if (block.type === 'callout') {
        const haystack = `${block.title ?? ''} ${block.text}`
        if (haystack.toLowerCase().includes(q)) {
          hits.push({
            pageId: page.id,
            pageTitle: page.title,
            category: page.category,
            snippet: excerpt(haystack, q),
          })
        }
      }

      if (block.type === 'figure') {
        const haystack = `${block.caption} ${block.alt} ${block.placeholderNote ?? ''}`
        if (haystack.toLowerCase().includes(q)) {
          hits.push({
            pageId: page.id,
            pageTitle: page.title,
            category: page.category,
            snippet: excerpt(block.caption, q),
          })
        }
      }

      if (block.type === 'code' && block.code.toLowerCase().includes(q)) {
        hits.push({
          pageId: page.id,
          pageTitle: page.title,
          category: page.category,
          snippet: excerpt(block.code, q),
        })
      }
    }
  }

  return hits.slice(0, limit)
}

function excerpt(text: string, query: string, radius = 70): string {
  const lower = text.toLowerCase()
  const at = lower.indexOf(query)
  if (at < 0) return text.slice(0, radius * 2)
  const start = Math.max(0, at - radius)
  const end = Math.min(text.length, at + query.length + radius)
  return `${start > 0 ? '…' : ''}${text.slice(start, end)}${end < text.length ? '…' : ''}`
}
